import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import { Backdrop, CircularProgress } from '@mui/material';
import { AlertTriangle, X } from 'lucide-react';

const ExpectationsModal = ({ isOpen, updateExpectationsCount, onClose, userId }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [backopen, setBackopen] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    mode: 'onSubmit',
    defaultValues: {
      expectations: '',
      challenges: '',
      expert_focus: '',
      consent: false,
    },
  });

  const watchExpectations = watch('expectations');
  const watchChallenges = watch('challenges');
  const watchExpertFocus = watch('expert_focus');

  const LoaderClose = () => {
    setBackopen(false);
  };

  const LoaderOpen = () => {
    setBackopen(true);
  };

  // Fetch existing expectations data if available
  useEffect(() => {
    if (isOpen && userId) {
      const fetchExpectationsData = async () => {
        LoaderOpen();
        try {
          const response = await axios.post(
            `${process.env.REACT_APP_API_ENDPOINT_NODE_PRODUCTION_DASHBOARD}/profile/fetchIITKUserDetails`,
            { user_id: userId }
          );

          if (response.data.status === 200) {
            const fetchedData = response.data.data[0];

            if (fetchedData.user_expectations) {
              setValue('expectations', fetchedData.user_expectations);
            }
            if (fetchedData.user_challenges) {
              setValue('challenges', fetchedData.user_challenges);
            }
            if (fetchedData.user_expert_focus) {
              setValue('expert_focus', fetchedData.user_expert_focus);
            }
            if (fetchedData.user_consent) {
              setValue('consent', fetchedData.user_consent === '1' || fetchedData.user_consent === 1);
            }
          }
        } catch (error) {
          console.error('Error fetching expectations data:', error);
        }
        LoaderClose();
      };

      fetchExpectationsData();
    }
  }, [isOpen, userId, setValue]);

  const handleSkip = async () => {
    if (updateExpectationsCount) {

      try {
        // Get current localStorage skip count
        const localSkipCount = parseInt(localStorage.getItem('expectation_skip_count') || '0');

        // Increment and save to localStorage
        const newLocalSkipCount = localSkipCount + 1;
        localStorage.setItem('expectation_skip_count', newLocalSkipCount.toString());

        // Update backend skip count
        await axios.post(
          `${process.env.REACT_APP_API_ENDPOINT_NODE_PRODUCTION_DASHBOARD}/profile/updateExpectationSkipStatus`,
          {
            user_id: userId,
          }
        );

        onClose();
      } catch (error) {
        console.error('Error updating skip status:', error);
        onClose();
      }
    } else {
      onClose();
    }
  };

  const onSubmit = async (formData) => {
    setIsSubmitting(true);
    LoaderOpen();

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT_NODE_PRODUCTION_DASHBOARD}/profile/updateExpectationsDetails`,
        {
          user_id: userId,
          user_expectations: formData.expectations,
          user_challenges: formData.challenges,
          user_expert_focus: formData.expert_focus,
          user_consent: formData.consent ? 1 : 0,
        }
      );

      if (response.data.status === 200) {
        LoaderClose();
        toast.success('Your expectations have been saved successfully!', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'colored',
        });

        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        LoaderClose();
        Swal.fire({
          title: 'Oops!',
          text: 'There was an error saving your expectations. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      }
    } catch (error) {
      LoaderClose();
      console.error('Error submitting expectations:', error);
      Swal.fire({
        title: 'Oops!',
        text: 'There was an error saving your expectations. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    } finally {
      LoaderClose();
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Modal Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-60 z-[9999] flex items-center justify-center p-4 animate-fadeIn">
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col  animate-slideUp">
          {/* Modal Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-8 py-6 relative">
            <button
              onClick={handleSkip}
              className="absolute top-4 right-4 text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-all"
              disabled={isSubmitting}
            >
              <X size={24} />
            </button>
            <h2 className="text-3xl font-bold text-white">
              Program Expectations
            </h2>
            <p className="text-blue-100 mt-2 text-base">
              Help us understand your expectations and tailor the program to your needs
            </p>
          </div>

          {/* Modal Body */}
          <div className="overflow-y-auto flex-1 px-8 py-6">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Expectations Field */}
              <div>
                <label className="block text-base font-semibold text-gray-800 mb-2">
                  What are your expectations from this program? <span className="text-red-500">*</span>
                </label>
                <textarea
                  {...register('expectations', {
                    required: 'This field is required',
                    minLength: {
                      value: 50,
                      message: 'Please provide at least 50 characters',
                    },
                    maxLength: {
                      value: 400,
                      message: 'Maximum 400 characters allowed',
                    },
                  })}
                  rows={4}
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none transition-all ${errors.expectations
                    ? 'border-red-400 bg-red-50'
                    : 'border-gray-300 hover:border-gray-400'
                    }`}
                  placeholder="Share your expectations from this program..."
                  disabled={isSubmitting}
                />
                <div className="flex justify-between items-center mt-2">
                  <div>
                    {errors.expectations && (
                      <div className="flex items-center gap-2 text-red-600 text-sm font-medium">
                        <AlertTriangle size={16} className="flex-shrink-0" />
                        <span>{errors.expectations.message}</span>
                      </div>
                    )}
                  </div>
                  <span className={`text-sm ${watchExpectations?.length >= 400 ? 'text-red-500 font-semibold' : 'text-gray-500'}`}>
                    {watchExpectations?.length || 0}/400
                  </span>
                </div>
              </div>

              {/* Challenges Field */}
              <div>
                <label className="block text-base font-semibold text-gray-800 mb-2">
                  What challenges are you currently facing in your professional journey? <span className="text-red-500">*</span>
                </label>
                <textarea
                  {...register('challenges', {
                    required: 'This field is required',
                    minLength: {
                      value: 50,
                      message: 'Please provide at least 50 characters',
                    },
                    maxLength: {
                      value: 400,
                      message: 'Maximum 400 characters allowed',
                    },
                  })}
                  rows={4}
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none transition-all ${errors.challenges
                    ? 'border-red-400 bg-red-50'
                    : 'border-gray-300 hover:border-gray-400'
                    }`}
                  placeholder="Describe the challenges you're facing..."
                  disabled={isSubmitting}
                />
                <div className="flex justify-between items-center mt-2">
                  <div>
                    {errors.challenges && (
                      <div className="flex items-center gap-2 text-red-600 text-sm font-medium">
                        <AlertTriangle size={16} className="flex-shrink-0" />
                        <span>{errors.challenges.message}</span>
                      </div>
                    )}
                  </div>
                  <span className={`text-sm ${watchChallenges?.length >= 400 ? 'text-red-500 font-semibold' : 'text-gray-500'}`}>
                    {watchChallenges?.length || 0}/400
                  </span>
                </div>
              </div>

              {/* Expert Focus Field */}
              <div>
                <label className="block text-base font-semibold text-gray-800 mb-2">
                  What areas would you like our experts to focus on? <span className="text-red-500">*</span>
                </label>
                <textarea
                  {...register('expert_focus', {
                    required: 'This field is required',
                    minLength: {
                      value: 50,
                      message: 'Please provide at least 50 characters',
                    },
                    maxLength: {
                      value: 400,
                      message: 'Maximum 400 characters allowed',
                    },
                  })}
                  rows={4}
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none transition-all ${errors.expert_focus
                    ? 'border-red-400 bg-red-50'
                    : 'border-gray-300 hover:border-gray-400'
                    }`}
                  placeholder="Specify areas where you need expert guidance..."
                  disabled={isSubmitting}
                />
                <div className="flex justify-between items-center mt-2">
                  <div>
                    {errors.expert_focus && (
                      <div className="flex items-center gap-2 text-red-600 text-sm font-medium">
                        <AlertTriangle size={16} className="flex-shrink-0" />
                        <span>{errors.expert_focus.message}</span>
                      </div>
                    )}
                  </div>
                  <span className={`text-sm ${watchExpertFocus?.length >= 400 ? 'text-red-500 font-semibold' : 'text-gray-500'}`}>
                    {watchExpertFocus?.length || 0}/400
                  </span>
                </div>
              </div>

              {/* Consent Checkbox */}
              <div className={`border-2 rounded-xl p-4 transition-all ${errors.consent ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50'
                }`}>
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    {...register('consent', {
                      required: 'You must provide consent to continue',
                    })}
                    className="mt-1 h-5 w-5 text-blue-600 focus:ring-2 focus:ring-blue-500 border-gray-300 rounded cursor-pointer disabled:cursor-not-allowed"
                    id="consent-checkbox"
                    disabled={isSubmitting}
                  />
                  <label htmlFor="consent-checkbox" className="text-sm text-gray-700 leading-relaxed cursor-pointer">
                    I consent to share this information with the program faculty
                    and acknowledge that this will help tailor the program to my needs. <span className="text-red-500">*</span>
                  </label>
                </div>
                {errors.consent && (
                  <div className="flex items-center gap-2 text-red-600 text-sm font-medium mt-2 ml-8">
                    <AlertTriangle size={16} className="flex-shrink-0" />
                    <span>{errors.consent.message}</span>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="flex flex-col sm:flex-row justify-end gap-3 pt-6 border-t-2 border-gray-200">
                <button
                  type="button"
                  onClick={handleSkip}
                  className="px-8 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl font-semibold transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  Skip for Now
                </button>
                <button
                  type="submit"
                  className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 rounded-xl font-semibold transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <CircularProgress size={20} color="inherit" />
                      <span>Submitting...</span>
                    </span>
                  ) : (
                    'Submit Expectations'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Loading Backdrop - Higher z-index to appear above modal */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: 99999,
          backgroundColor: 'rgba(0, 0, 0, 0.7)'
        }}
        open={backopen}
      >
        <div className="flex flex-col items-center gap-4">
          <CircularProgress size={60} color="inherit" thickness={4} />
          <p className="text-white text-lg font-semibold">Please wait while we submit your expectations...</p>
        </div>
      </Backdrop>

      <ToastContainer />

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            transform: translateY(30px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }

        .animate-slideUp {
          animation: slideUp 0.4s ease-out;
        }
      `}</style>
    </>
  );
};

export default ExpectationsModal;
